import React from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import { Title, Text, Button } from '@tremor/react';
import { SparklesIcon, DocumentTextIcon } from '@heroicons/react/24/outline';
import { useBehavioralArtifact } from './useBehavioralArtifact';
import UpgradedLoader from '../UpgradedLoader';

/**
 * Part 3: a general Behavioral Economics report to read BEFORE data collection.
 */
function BeReportTab({ projectUuid, analysisUuid }) {
    const { t } = useTranslation();
    const { status, result, loading, generate } = useBehavioralArtifact(projectUuid, analysisUuid, 'be_report');

    // Strip a wrapping ```markdown ... ``` fence if the AI added one (else it renders as a
    // literal code block instead of formatted markdown).
    const rawMarkdown = result?.markdown ?? '';
    const fenceMatch = rawMarkdown.match(/^```[a-zA-Z]*\n([\s\S]*?)\n```\s*$/);
    const markdown = fenceMatch ? fenceMatch[1] : rawMarkdown;

    return (
        <div data-testid="be-report-tab">
            <div className="flex items-center justify-between mb-2">
                <div className="flex items-center space-x-3">
                    <DocumentTextIcon className="h-6 w-6 text-iavisus-100" />
                    <Title className="text-lg font-semibold">{t('BehavioralEconomics.Report.Title')}</Title>
                </div>
                {status !== 'processing' && (
                    <Button icon={SparklesIcon} onClick={generate} className="bg-iavisus-900">
                        {status === 'completed' ? t('BehavioralEconomics.Regenerate') : t('BehavioralEconomics.Generate')}
                    </Button>
                )}
            </div>
            <Text className="mb-4">{t('BehavioralEconomics.Report.Description')}</Text>

            {(loading || status === 'processing') && <UpgradedLoader />}
            {status === 'failed' && <Text className="text-red-600">{t('BehavioralEconomics.Error')}</Text>}
            {status === 'not_started' && !loading && <Text className="text-gray-500">{t('BehavioralEconomics.Report.Empty')}</Text>}

            {status === 'completed' && markdown && (
                <article className="prose prose-sm max-w-none prose-headings:text-iavisus-100">
                    <ReactMarkdown>{markdown}</ReactMarkdown>
                </article>
            )}
        </div>
    );
}

export default BeReportTab;
